import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { localStorageService } from "../../../shared/api/localstorage-service";
import { uploadFormDataWithProgress } from "../../../shared/lib/upload-form-data-with-progress";
import { BaseResponse } from "../../../shared/types";
import {
  CategoriesResponse,
  CategoryResponse,
  CreateCategoryRequest,
  CreateEventRequest,
  CreateEventPriceRequest,
  CreateQRCodeRequest,
  CreateSkillRequest,
  CreateSpeakerRequest,
  EventListResponse,
  EventPriceResponse,
  EventPricesResponse,
  EventResponse,
  EventContentListResponse,
  EventContentResponse,
  CreateEventContentLinkRequest,
  UpdateEventContentFileRequest,
  UpdateEventContentLinkRequest,
  UpdateEventPriceRequest,
  QRCodeResponse,
  QRCodesResponse,
  SkillResponse,
  SkillsResponse,
  SpeakerResponse,
  SpeakersResponse,
  UpdateCategoryRequest,
  UpdateEventRequest,
  UpdateSkillRequest,
  UpdateSpeakerRequest,
  UrlResponse,
  EventStatsResponse,
  EventApplication,
  EventApplicationsResponse,
  EventApplicationsCopyResponse,
} from "./types";

export type EventSort =
  | "startat"
  | "format"
  | "city"
  | "applicationCount"
  | "participantCount";

export interface GetEventsParams {
  search?: string;
  from?: string;
  to?: string;
  cities?: number[];
  categories?: number[];
  formats?: ("online" | "offline")[];
  clubs?: number[];
  participantsFormed?: boolean;
  sort?: EventSort;
  order?: "asc" | "desc";
  pageToken?: string;
  limit?: number;
}

export const eventsApi = createApi({
  reducerPath: "eventsApi",
  baseQuery: baseApiQuery,
  tagTypes: [
    "Events",
    "EventCategories",
    "EventSpeakers",
    "EventSkills",
    "EventQRCodes",
    "EventContent",
    "EventPrices",
    "EventApplications",
  ],
  endpoints: (builder) => ({
    // Events endpoints
    getEvents: builder.query<EventListResponse, GetEventsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append("search", params.search);
        if (params.from) queryParams.append("from", params.from);
        if (params.to) queryParams.append("to", params.to);
        if (params.cities?.length) {
          params.cities.forEach((city) =>
            queryParams.append("cities", city.toString())
          );
        }
        if (params.categories?.length) {
          params.categories.forEach((category) =>
            queryParams.append("categories", category.toString())
          );
        }
        if (params.formats?.length) {
          params.formats.forEach((format) =>
            queryParams.append("formats", format)
          );
        }
        if (params.clubs?.length) {
          params.clubs.forEach((club) =>
            queryParams.append("clubs", club.toString())
          );
        }
        if (params.participantsFormed !== undefined) {
          queryParams.append(
            "participantsFormed",
            params.participantsFormed.toString()
          );
        }
        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.pageToken) queryParams.append("pageToken", params.pageToken);
        if (params.limit) queryParams.append("limit", params.limit.toString());

        return `/admin/events${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["Events"],
    }),

    getEvent: builder.query<EventResponse, number>({
      query: (id) => `/admin/events/${id}`,
      providesTags: ["Events"],
    }),

    getEventStats: builder.query<EventStatsResponse, number>({
      query: (id) => `/admin/events/${id}/stats`,
      providesTags: ["Events"],
    }),

    createEvent: builder.mutation<EventResponse, CreateEventRequest>({
      query: (body) => ({
        url: "/admin/events",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Events"],
    }),

    updateEvent: builder.mutation<
      EventResponse,
      { id: number; body: UpdateEventRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Events"],
    }),

    deleteEvent: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Events"],
    }),

    publishEvent: builder.mutation<EventResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}/publish`,
        method: "PUT",
      }),
      invalidatesTags: ["Events"],
    }),

    uploadEventThumbnail: builder.mutation<
      UrlResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/events/${id}/thumbnail`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["Events"],
    }),

    deleteEventThumbnail: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}/thumbnail`,
        method: "DELETE",
      }),
      invalidatesTags: ["Events"],
    }),

    uploadEventLargeImage: builder.mutation<
      UrlResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/events/${id}/large`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["Events"],
    }),

    deleteEventLargeImage: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}/large`,
        method: "DELETE",
      }),
      invalidatesTags: ["Events"],
    }),

    // QR Codes endpoints
    getEventQRCodes: builder.query<QRCodesResponse, number>({
      query: (id) => `/admin/events/${id}/qrcodes`,
      providesTags: ["EventQRCodes"],
    }),

    createEventQRCode: builder.mutation<
      QRCodeResponse,
      { id: number; body: CreateQRCodeRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/${id}/qrcodes`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["EventQRCodes"],
    }),

    deleteEventQRCode: builder.mutation<
      BaseResponse,
      { id: number; code: string }
    >({
      query: ({ id, code }) => ({
        url: `/admin/events/${id}/qrcodes/${code}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventQRCodes"],
    }),

    // Categories endpoints
    getEventCategories: builder.query<CategoriesResponse, void>({
      query: () => "/admin/events/categories",
      providesTags: ["EventCategories"],
    }),

    createEventCategory: builder.mutation<
      CategoryResponse,
      CreateCategoryRequest
    >({
      query: (body) => ({
        url: "/admin/events/categories",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EventCategories"],
    }),

    updateEventCategory: builder.mutation<
      CategoryResponse,
      { id: number; body: UpdateCategoryRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/categories/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventCategories"],
    }),

    deleteEventCategory: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventCategories"],
    }),

    // Speakers endpoints
    getEventSpeakers: builder.query<SpeakersResponse, void>({
      query: () => "/admin/events/speakers",
      providesTags: ["EventSpeakers"],
    }),

    createEventSpeaker: builder.mutation<SpeakerResponse, CreateSpeakerRequest>(
      {
        query: (body) => ({
          url: "/admin/events/speakers",
          method: "POST",
          body,
        }),
        invalidatesTags: ["EventSpeakers"],
      }
    ),

    updateEventSpeaker: builder.mutation<
      SpeakerResponse,
      { id: number; body: UpdateSpeakerRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/speakers/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventSpeakers"],
    }),

    exportEventSpeakersXlsx: builder.query<Blob, void>({
      query: () => ({
        url: "/admin/events/speakers/export/xlsx",
        method: "GET",
        responseHandler: async (response) => response.blob(),
      }),
    }),

    deleteEventSpeaker: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/speakers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventSpeakers"],
    }),

    uploadEventSpeakerAvatar: builder.mutation<
      UrlResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/events/speakers/${id}/avatar`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["EventSpeakers"],
    }),

    deleteEventSpeakerAvatar: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/speakers/${id}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventSpeakers"],
    }),

    // Skills endpoints
    getEventSpeakerSkills: builder.query<SkillsResponse, void>({
      query: () => "/admin/events/speakers/skills",
      providesTags: ["EventSkills"],
    }),

    createEventSpeakerSkill: builder.mutation<
      SkillResponse,
      CreateSkillRequest
    >({
      query: (body) => ({
        url: "/admin/events/speakers/skills",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EventSkills"],
    }),

    updateEventSpeakerSkill: builder.mutation<
      SkillResponse,
      { id: number; body: UpdateSkillRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/speakers/skills/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventSkills"],
    }),

    deleteEventSpeakerSkill: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/speakers/skills/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventSkills"],
    }),

    getEventContent: builder.query<EventContentListResponse, number>({
      query: (id) => `/admin/events/${id}/content`,
      providesTags: ["EventContent"],
    }),

    uploadEventContentFile: builder.mutation<
      EventContentResponse,
      {
        id: number;
        file: FormData;
        onProgress?: (progress: number) => void;
        onUploadComplete?: () => void;
      }
    >({
      async queryFn({ id, file, onProgress, onUploadComplete }) {
        try {
          const token = await localStorageService.getToken();
          const baseUrl = import.meta.env.VITE_API_BASE_URL;
          const data = await uploadFormDataWithProgress<EventContentResponse>(
            `${baseUrl}/admin/events/${id}/content/files`,
            file,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              onProgress,
              onUploadComplete,
            },
          );

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Upload failed",
            },
          };
        }
      },
      invalidatesTags: ["EventContent"],
    }),

    createEventContentLink: builder.mutation<
      EventContentResponse,
      { id: number; body: CreateEventContentLinkRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/${id}/content/links`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["EventContent"],
    }),

    updateEventContentFile: builder.mutation<
      EventContentResponse,
      { id: number; contentId: number; body: UpdateEventContentFileRequest }
    >({
      query: ({ id, contentId, body }) => ({
        url: `/admin/events/${id}/content/files/${contentId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventContent"],
    }),

    updateEventContentLink: builder.mutation<
      EventContentResponse,
      { id: number; contentId: number; body: UpdateEventContentLinkRequest }
    >({
      query: ({ id, contentId, body }) => ({
        url: `/admin/events/${id}/content/links/${contentId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventContent"],
    }),

    deleteEventContent: builder.mutation<
      BaseResponse,
      { id: number; contentId: number }
    >({
      query: ({ id, contentId }) => ({
        url: `/admin/events/${id}/content/${contentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventContent"],
    }),

    // Event Prices endpoints
    getEventPrices: builder.query<EventPricesResponse, number>({
      query: (id) => `/admin/events/${id}/prices`,
      providesTags: ["EventPrices"],
    }),

    createEventPrice: builder.mutation<
      EventPriceResponse,
      { id: number; body: CreateEventPriceRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/events/${id}/prices`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["EventPrices"],
    }),

    updateEventPrice: builder.mutation<
      EventPriceResponse,
      {
        id: number;
        priceId: number;
        body: UpdateEventPriceRequest;
      }
    >({
      query: ({ id, priceId, body }) => ({
        url: `/admin/events/${id}/prices/${priceId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EventPrices"],
    }),

    deleteEventPrice: builder.mutation<
      BaseResponse,
      { id: number; priceId: number }
    >({
      query: ({ id, priceId }) => ({
        url: `/admin/events/${id}/prices/${priceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventPrices"],
    }),

    // Event Applications endpoints (состав участников)
    /**
     * Состав участников отдаётся постранично. Админке нужен список целиком
     * (иначе легко добавить человека, который уже есть на следующей странице),
     * поэтому страницы дочитываются здесь, а не в компоненте — так инвалидация
     * тега перезагружает сразу весь состав.
     */
    getEventApplications: builder.query<EventApplicationsResponse, number>({
      async queryFn(id, _api, _extra, fetchWithBQ) {
        const applications: EventApplication[] = [];
        let pageToken: string | undefined;
        // Страховка от бесконечного цикла, если бэкенд вернёт тот же токен
        let pagesLeft = 20;

        do {
          const params = new URLSearchParams({ limit: "100" });
          if (pageToken) params.append("pageToken", pageToken);

          const page = await fetchWithBQ(
            `/admin/events/${id}/applications?${params.toString()}`
          );
          if (page.error) return { error: page.error };

          const data = page.data as EventApplicationsResponse;
          applications.push(...(data.applications ?? []));
          pageToken = data.pageToken || undefined;
          pagesLeft -= 1;
        } while (pageToken && pagesLeft > 0);

        return { data: { result: true, applications } };
      },
      providesTags: ["EventApplications"],
    }),

    addEventApplication: builder.mutation<
      BaseResponse,
      { id: number; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/admin/events/${id}/applications/${userId}`,
        method: "POST",
      }),
      invalidatesTags: ["EventApplications", "Events"],
    }),

    removeEventApplication: builder.mutation<
      BaseResponse,
      { id: number; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/admin/events/${id}/applications/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EventApplications", "Events"],
    }),

    copyEventApplications: builder.mutation<
      EventApplicationsCopyResponse,
      { id: number; sourceEventId: number }
    >({
      query: ({ id, sourceEventId }) => ({
        url: `/admin/events/${id}/applications/copy/${sourceEventId}`,
        method: "POST",
      }),
      invalidatesTags: ["EventApplications", "Events"],
    }),

    sendEventPreviewEmail: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}/preview/email`,
        method: "POST",
      }),
    }),

    sendEventPreviewPush: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/events/${id}/preview/push`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventQuery,
  useGetEventStatsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  usePublishEventMutation,
  useUploadEventThumbnailMutation,
  useDeleteEventThumbnailMutation,
  useUploadEventLargeImageMutation,
  useDeleteEventLargeImageMutation,
  useGetEventQRCodesQuery,
  useCreateEventQRCodeMutation,
  useDeleteEventQRCodeMutation,
  useGetEventCategoriesQuery,
  useCreateEventCategoryMutation,
  useUpdateEventCategoryMutation,
  useDeleteEventCategoryMutation,
  useGetEventSpeakersQuery,
  useLazyExportEventSpeakersXlsxQuery,
  useCreateEventSpeakerMutation,
  useUpdateEventSpeakerMutation,
  useDeleteEventSpeakerMutation,
  useUploadEventSpeakerAvatarMutation,
  useDeleteEventSpeakerAvatarMutation,
  useGetEventSpeakerSkillsQuery,
  useCreateEventSpeakerSkillMutation,
  useUpdateEventSpeakerSkillMutation,
  useDeleteEventSpeakerSkillMutation,
  useGetEventContentQuery,
  useUploadEventContentFileMutation,
  useCreateEventContentLinkMutation,
  useUpdateEventContentFileMutation,
  useUpdateEventContentLinkMutation,
  useDeleteEventContentMutation,
  useGetEventPricesQuery,
  useCreateEventPriceMutation,
  useUpdateEventPriceMutation,
  useDeleteEventPriceMutation,
  useSendEventPreviewEmailMutation,
  useSendEventPreviewPushMutation,
  useGetEventApplicationsQuery,
  useAddEventApplicationMutation,
  useRemoveEventApplicationMutation,
  useCopyEventApplicationsMutation,
} = eventsApi;
